import { FC, ComponentProps, ReactNode, useId } from 'react';

import cx from 'classnames';

import { Checkbox as ShadcnCheckbox } from '../shadcn/Checkbox';
import { TStatus } from '@skillarea/lib-shared';

import { renderMessages, TCommonInputProps } from './useInputLogic';

// Tailwind classes for statuses and colors
const COLOR_CLASSES: Record<string, string> = {
  gray: 'border-slate-200/85 bg-slate-100/10 shadow-[0_0_0_1px_rgba(248,250,252,0.18)] hover:border-accent hover:bg-slate-100/15 data-[state=checked]:border-accent data-[state=checked]:bg-accent data-[state=checked]:text-slate-950 data-[state=checked]:shadow-none',
  white:
    'border-2 border-white/90 bg-white/12 shadow-[0_0_0_1px_rgba(255,255,255,0.22)] hover:bg-white/18 data-[state=checked]:border-white data-[state=checked]:bg-white data-[state=checked]:text-slate-950 data-[state=checked]:shadow-none',
};

const STATUS_CLASSES: Record<string, string> = {
  error:
    'border-destructive bg-destructive/10 data-[state=checked]:bg-destructive data-[state=checked]:text-destructive-foreground',
  success:
    'border-success data-[state=checked]:bg-success data-[state=checked]:text-success-foreground',
  warning:
    'border-warning bg-warning/10 data-[state=checked]:bg-warning data-[state=checked]:text-warning-foreground',
  info: 'border-info data-[state=checked]:bg-info data-[state=checked]:text-info-foreground',
};

// Size via Tailwind classes
const SIZE_CLASSES = {
  bold: 'h-7 w-7',
  default: 'h-5 w-5',
};

type TInputCheckboxProps = {
  color?: 'gray' | 'white';
  disabled?: boolean;
  isBold?: boolean;
  label?: ReactNode;
  onChange?: (v: boolean) => void;
  status?: TStatus;
  value?: boolean;
} & Pick<
  TCommonInputProps,
  'classes' | 'isHidden' | 'isMessagesRemoved' | 'messages'
> &
  Omit<
    ComponentProps<typeof ShadcnCheckbox>,
    'checked' | 'className' | 'onCheckedChange'
  > & {
    className?: string;
  };

export const InputCheckbox: FC<TInputCheckboxProps> = ({
  className,
  classes,
  color = 'gray',
  disabled,
  id,
  isHidden,
  isMessagesRemoved,
  isBold,
  label,
  messages,
  name,
  onBlur,
  onChange,
  onFocus,
  status,
  value,
  ...checkboxProps
}) => {
  const generatedId = useId();
  const checkboxId = id ?? generatedId;
  const sizeClass = isBold ? SIZE_CLASSES.bold : SIZE_CLASSES.default;

  const checkboxClassName = cx(
    'shrink-0 rounded-sm border ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors',
    sizeClass,
    !status && COLOR_CLASSES[color],
    status && STATUS_CLASSES[status],
    'data-[state=checked]:text-primary-foreground shadow-sm',
  );

  const renderMessagesComponent = renderMessages(
    {
      classes,
      messages,
      status,
    },
    false,
  );

  return (
    <div
      className={cx(
        'flex flex-col gap-1',
        isHidden && 'visually-hidden',
        className,
      )}
    >
      <label
        htmlFor={checkboxId}
        className="flex items-start gap-3 cursor-pointer"
      >
        <ShadcnCheckbox
          {...checkboxProps}
          checked={value ?? false}
          disabled={disabled}
          className={checkboxClassName}
          id={checkboxId}
          name={name}
          onBlur={onBlur}
          onCheckedChange={(checked) => {
            onChange?.(checked === true);
          }}
          onFocus={onFocus}
        />
        {label && <div className="flex-1 pt-0.5">{label}</div>}
      </label>
      {!isMessagesRemoved && renderMessagesComponent}
    </div>
  );
};
